import React from 'react';
import { Helmet } from 'react-helmet-async';

// SEO component for vehicle detail pages
export const VehicleSEO = ({ vehicle, baseUrl }) => {
  if (!vehicle) return null;

  const vehicleSchema = {
    "@context": "https://schema.org",
    "@type": "Vehicle",
    "name": vehicle.title,
    "description": vehicle.description || `${vehicle.year} ${vehicle.make} ${vehicle.model} - ${vehicle.condition} vehicle for sale at Big Chief Motors`,
    "brand": {
      "@type": "Brand",
      "name": vehicle.make
    },
    "model": vehicle.model,
    "vehicleModelDate": vehicle.year?.toString(),
    "color": vehicle.exteriorColor,
    "vehicleInteriorColor": vehicle.interiorColor,
    "vehicleTransmission": vehicle.transmission,
    "driveWheelConfiguration": vehicle.drivetrain,
    "fuelType": vehicle.fuelType,
    "vehicleEngine": {
      "@type": "EngineSpecification",
      "name": vehicle.engine
    },
    "numberOfDoors": vehicle.doors,
    "vehicleSeatingCapacity": vehicle.seats,
    "bodyType": vehicle.bodyType,
    "mileageFromOdometer": {
      "@type": "QuantitativeValue",
      "value": vehicle.km,
      "unitCode": "KMT"
    },
    "vehicleIdentificationNumber": vehicle.vin,
    "itemCondition": vehicle.condition === "New" 
      ? "https://schema.org/NewCondition" 
      : "https://schema.org/UsedCondition",
    "offers": {
      "@type": "Offer",
      "price": vehicle.price,
      "priceCurrency": "CAD",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "AutoDealer",
        "name": "Big Chief Motors",
        "telephone": "1-800-985-2117"
      }
    },
    "image": vehicle.images && vehicle.images.length > 0 
      ? vehicle.images.map(img => img.startsWith('http') ? img : `${baseUrl}${img}`)
      : undefined,
    "url": `${baseUrl}/vehicle/${vehicle.id}`
  };

  // BreadcrumbList schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Inventory",
        "item": `${baseUrl}/inventory`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": vehicle.title,
        "item": `${baseUrl}/vehicle/${vehicle.id}`
      }
    ]
  };

  const pageTitle = `${vehicle.title} | Big Chief Motors`;
  const pageDescription = vehicle.description || 
    `${vehicle.year} ${vehicle.make} ${vehicle.model} for sale at Big Chief Motors. ${vehicle.km?.toLocaleString()} km, ${vehicle.transmission}, ${vehicle.drivetrain}. Price: $${vehicle.price?.toLocaleString()}`;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={`${vehicle.make}, ${vehicle.model}, ${vehicle.year}, ${vehicle.bodyType}, used cars, Big Chief Motors`} />
      
      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content="product" />
      <meta property="og:url" content={`${baseUrl}/vehicle/${vehicle.id}`} />
      {vehicle.images && vehicle.images[0] && (
        <meta property="og:image" content={vehicle.images[0].startsWith('http') ? vehicle.images[0] : `${baseUrl}${vehicle.images[0]}`} />
      )}
      <meta property="product:price:amount" content={vehicle.price} />
      <meta property="product:price:currency" content="CAD" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={`${baseUrl}/vehicle/${vehicle.id}`} />
      
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(vehicleSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};

// SEO component for inventory page
export const InventorySEO = ({ baseUrl, vehicleCount, title, description, path = '/inventory' }) => {
  const pageTitle = title || 'Vehicle Inventory | Big Chief Motors';
  const pageDesc = description || 'Browse our selection of quality used trucks, SUVs, sedans, ATVs, and more. Find your perfect vehicle at Big Chief Motors.';
  const url = `${baseUrl}${path}`;
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": pageTitle,
    "description": pageDesc,
    "numberOfItems": vehicleCount || 0,
    "url": url
  };

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <link rel="canonical" href={url} />
      <script type="application/ld+json">
        {JSON.stringify(itemListSchema)}
      </script>
    </Helmet>
  );
};

// SEO component for financing page
export const FinancingSEO = ({ baseUrl }) => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What credit score do I need to get approved?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We work with all credit types including excellent, good, fair, and rebuilding credit. Our financing specialists can help find options that work for your situation."
        }
      },
      {
        "@type": "Question",
        "name": "How long does the approval process take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most applications receive a response within 24-48 hours. Pre-approval can often be completed the same day."
        }
      },
      {
        "@type": "Question",
        "name": "What documents do I need for financing?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "You'll typically need proof of income, proof of residence, valid driver's license, and references. Our team will guide you through the specific requirements."
        }
      }
    ]
  };

  return (
    <Helmet>
      <title>Apply Now | Big Chief Motors</title>
      <meta name="description" content="Apply now with Big Chief Motors. We work with all credit types and offer a quick approval process with competitive rates." />
      <meta property="og:title" content="Apply Now | Big Chief Motors" />
      <link rel="canonical" href={`${baseUrl}/apply-now`} />
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </Helmet>
  );
};

// SEO component for contact page
export const ContactSEO = ({ baseUrl }) => {
  return (
    <Helmet>
      <title>Contact Us | Big Chief Motors</title>
      <meta name="description" content="Get in touch with Big Chief Motors. Call 1-800-985-2117 or visit us at our dealership. We're here to help you find your perfect vehicle." />
      <meta property="og:title" content="Contact Us | Big Chief Motors" />
      <meta property="og:description" content="Get in touch with Big Chief Motors. Call 1-800-985-2117 or visit our dealership." />
      <meta property="og:type" content="website" />
      <link rel="canonical" href={`${baseUrl}/contact`} />
    </Helmet>
  );
};

// SEO component for homepage
export const HomepageSEO = ({ baseUrl }) => {
  const autoDealerSchema = {
    "@context": "https://schema.org",
    "@type": "AutoDealer",
    "name": "Big Chief Motors",
    "description": "Winnipeg's trusted vehicle dealership offering quality used trucks, SUVs, sedans, ATVs, and more with easy financing and no hidden fees.",
    "url": baseUrl,
    "telephone": "1-800-985-2117",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Winnipeg",
      "addressRegion": "MB",
      "addressCountry": "CA"
    },
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "09:00", "closes": "19:00" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "10:00", "closes": "17:00" }
    ],
    "priceRange": "$$",
    "image": `${baseUrl}/logo.jpg`,
    "sameAs": []
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Big Chief Motors",
    "url": baseUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${baseUrl}/inventory?search={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <title>Big Chief Motors | Used Trucks, SUVs & Cars in Winnipeg</title>
      <meta name="description" content="Big Chief Motors - Winnipeg's trusted vehicle dealership. Browse quality used trucks, SUVs, sedans, ATVs & more. Easy financing, no hidden fees. Get pre-approved today!" />
      <meta name="keywords" content="used cars Winnipeg, trucks for sale Manitoba, SUV dealership, auto financing Winnipeg, Big Chief Motors, used trucks Winnipeg" />
      <meta property="og:title" content="Big Chief Motors | Used Trucks, SUVs & Cars in Winnipeg" />
      <meta property="og:description" content="Browse quality used trucks, SUVs, sedans & more. Easy financing, no hidden fees." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={baseUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <link rel="canonical" href={baseUrl} />
      <script type="application/ld+json">{JSON.stringify(autoDealerSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
    </Helmet>
  );
};

// SEO component for about page
export const AboutSEO = ({ baseUrl }) => {
  return (
    <Helmet>
      <title>About Us | Big Chief Motors - Winnipeg's Trusted Dealership</title>
      <meta name="description" content="Learn about Big Chief Motors - Winnipeg's trusted vehicle dealership. Quality vehicles, transparent pricing, and exceptional customer service since day one." />
      <meta property="og:title" content="About Big Chief Motors" />
      <meta property="og:description" content="Winnipeg's trusted vehicle dealership with quality vehicles and transparent pricing." />
      <meta property="og:type" content="website" />
      <link rel="canonical" href={`${baseUrl}/about`} />
    </Helmet>
  );
};

export default VehicleSEO;
